// Level_ModUI.tsx — Buildings Set1 §8 editor for `Mod_Level`. Surfaces `floorNumbers` (a chip
// list with an "Auto-fill X..Y" button) plus `floorThickness`/`wallHeight` via `InheritableField`.
// P1 has NO geometry — only authoring.
import {observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {Button, Row, Spinner, Text} from "react-vcomponents";
import {ES} from "web-vcore";
import {ObjModifier} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {Mod_Level} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_Level.js";
import {ResolveBuildingDefaults, ResolveLevelParams} from "../../../../../Server/DB/mapObjects/BuildingAccessors.js";
import {ModifierUI_SharedProps} from "../Modifiers_CompUI.js";
import {InheritableField} from "./Building/InheritableField.js";

export const Level_ModUI = observer_mgl((props: {mod: ObjModifier} & ModifierUI_SharedProps)=>{
	const {modWrapper: mod, ChangeModWrapper, obj} = props;
	const Change = (mutator: (data: Mod_Level)=>any)=>ChangeModWrapper(a=>mutator(a.level!));
	const data = mod.level!;

	const resolved = ResolveLevelParams(obj);
	const resolvedDefaults = ResolveBuildingDefaults(obj);
	const prov = (path: string)=>resolvedDefaults.provenance[path];

	const [fillFrom, setFillFrom] = useState(1);
	const [fillTo, setFillTo] = useState(3);

	const floorNumbers = (data.floorNumbers ?? []).filter(n=>n != null);
	const hasZero = (data.floorNumbers ?? []).some(n=>n == 0);

	return (
		<>
			<Text style={{color: "#999", fontSize: 12}}>A floor of a building; owns the subtree of Rooms below it. P1: authoring only — stacking lands in P2.</Text>

			<Row mt={5} style={{alignItems: "center", gap: 4}}>
				<Text style={{color: "#ccc"}}>Floor numbers (1-based):</Text>
				<Row style={{flexWrap: "wrap", gap: 3, marginLeft: 5}}>
					{floorNumbers.map((n, i)=>
						<Button key={n} p="2px 6px" text={String(n)}
							title="Remove this floor number"
							onClick={()=>Change(a=>{ a.floorNumbers = a.floorNumbers.filter(x=>x != n); })}/>
					)}
					<Button mdIcon="plus" title="Add a floor number"
						onClick={()=>{
							const next = (floorNumbers.length ? Math.max(...floorNumbers) : 0) + 1;
							Change(a=>a.floorNumbers = [...(a.floorNumbers ?? []), next]);
						}}/>
				</Row>
			</Row>
			{hasZero && <Text style={{color: "#f88", fontSize: 11}}>⚠ 0 is invalid (basements are -1, -2, …) and will be dropped.</Text>}

			<Row mt={5} style={{alignItems: "center", gap: 4}}>
				<Text style={{color: "#ccc"}}>Auto-fill:</Text>
				<Spinner step={1} value={fillFrom} onChange={val=>setFillFrom(val)}/>
				<Text>…</Text>
				<Spinner step={1} value={fillTo} onChange={val=>setFillTo(val)}/>
				<Button text="Apply" title="Populate this level at every floor number from X to Y (e.g. a prototype level for all floors of a tower)"
					onClick={()=>{
						const lo = Math.min(fillFrom, fillTo), hi = Math.max(fillFrom, fillTo);
						const range: number[] = [];
						for (let n = lo; n <= hi; n++) if (n != 0) range.push(n);
						Change(a=>a.floorNumbers = [...new Set([...(a.floorNumbers ?? []), ...range])].sort((x,y)=>x-y));
					}}/>
			</Row>

			{(()=>{
				const f = (key: keyof Mod_Level, label: string, number: {min?: number; max?: number; step?: number})=>{
					const resolvedValue = resolved[key] as number;
					const provenance = prov(`level.${key}`);
					return <InheritableField key={key} label={label} info="Inherited via the building fallback chain."
						resolvedValue={resolvedValue} value={data[key] ?? null}
						provenance={provenance}
						onChange={(v)=>Change(a=>{ (a as any)[key] = v as number|n; })}
						number={number}/>;
				};
				return <>
					{f("floorThickness", "Floor thickness (m)", {min: 0.05, max: 2, step: 0.05})}
					{f("wallHeight", "Wall height (m)", {min: 1.5, max: 20, step: 0.1})}
					{/* Set2 §4.1: a per-level wall thickness for every room on this level's floors. It maps
					    onto the SINGLE `room.wallThickness` leaf, so it slots between the room's own
					    override and the building default. */}
					<InheritableField label="Wall thickness (m)" info="Default wall thickness for every Room on this level's floors; a Room's own value still overrides it."
						resolvedValue={resolvedDefaults.values.room.wallThickness} value={data.wallThickness ?? null}
						provenance={prov("room.wallThickness")}
						onChange={(v)=>Change(a=>{ a.wallThickness = v as number|n; })}
						number={{min: 0.05, max: 2, step: 0.05}}/>
				</>;
			})()}
			<Text style={{color: "#999", fontSize: 11, marginTop: 4}}>Materials slot: full recipe picker lands in P6.</Text>
		</>
	);
});
